(function() {
    'use strict';

    angular
        .module('valueconnectApp')
        .factory('ReportBuilderNav', ReportBuilderNav);

    ReportBuilderNav.$inject = ['$window', '$q', 'AppraisalReport', 'AlertService'];

    /**
     * Where a "Build Appraisal Report" link goes, for the pages outside the order detail page that
     * offer one: the report header and the appraiser <-> VC message board.
     *
     * Both need the display order DTO (AppraisalOrder.get) and the full report DTO (AppraisalReport.get):
     * the Valique flags and the work flag are not on the minimal projections.
     */
    function ReportBuilderNav($window, $q, AppraisalReport, AlertService) {

        return {
            destination: destination,
            openInValique: openInValique
        };

        /**
         * 'VALIQUE', 'BUILDER', or null when there is nowhere to send the user.
         *
         * Valique for the products it builds, on the order's active report, once it is either already
         * backed by a Valique report or still able to receive one — seeding it on the first click.
         *
         * hasWorkThirdPartyWouldDiscard is the term that sends a half-built legacy report back to the
         * old builder. Seeding runs useThirdParty, which deactivates every form the appraiser has
         * filled in; the rows survive but nothing in the UI reaches them again. These links offer
         * Valique without a confirmation dialog, so they must not offer it at all when there is work to
         * lose. The order summary still does, behind its warning, which is where someone who genuinely
         * wants Valique goes.
         *
         * valiqueReportId is checked first and on its own: a report already over there opens there
         * whatever its forms look like now — seeding has already deactivated them, so the work test
         * would read false anyway, and this way the navigation does not depend on that coincidence.
         *
         * Note the two halves read different DTOs — valiqueReportId from activeReport, an
         * AppraisalReportListDTO, and the work flag from `report`, the full AppraisalReportDTO. That is
         * only coherent because of the identity check between them, which also keeps superseded reports
         * out of Valique.
         *
         * Otherwise the original builder, but only for a report that already holds a form there — a
         * legacy report in progress, an uploaded PDF, a legacy AVM. A new report starts with no form, and
         * the builder answers that with a form-selection dialog offering the legacy forms, so a report
         * with nothing on it gets no link at all: it is either Valique's or an upload.
         */
        function destination(order, report) {
            var activeReport = order.activeReport;
            var valique = order.reportBuilderEligible
                && activeReport && activeReport.id === report.id
                && (activeReport.valiqueReportId
                    || (order.reportBuilderSeedable && !report.hasWorkThirdPartyWouldDiscard));
            if (valique) {
                return 'VALIQUE';
            }
            var hasActiveForm = (report.forms || []).some(function(form) {
                return form.active === true;
            });
            return hasActiveForm ? 'BUILDER' : null;
        }

        /**
         * The server creates the Valique report on the first call and answers with its URL, so nothing
         * here knows Valique's routing or its deployed path prefix. Rejects when the page is not left.
         *
         * No destructive-conversion warning: destination() does not return VALIQUE when there is work
         * the conversion would discard.
         */
        function openInValique(reportId) {
            return AppraisalReport.reportBuilderLink({'id': reportId}, {}).$promise.then(
                function(result) {
                    if (result && result.url) {
                        $window.location.href = result.url;
                        return;
                    }
                    AlertService.error('Valique is not configured for this environment. Please contact Value Connect support.');
                    return $q.reject();
                },
                function(response) {
                    // A refusal (409) carries its own sentence, already shown by the global error
                    // interceptor; adding "try again" under it would be both duplicated and wrong.
                    var serverMessage = response && response.headers
                        && response.headers('X-valueconnectApp-error');
                    if (!serverMessage) {
                        AlertService.error('Could not open Valique. Please try again, or contact Value Connect support.');
                    }
                    return $q.reject(response);
                }
            );
        }
    }
})();
